import cx from 'classnames'
import { isEqual } from 'lodash'
import { observer } from 'mobx-react-lite'
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { UserAppPreferencesService } from 'api/services/UserAppPreferencesService'
import { useLongHover } from 'hooks/useLongHover'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { setFloatingSidebar } from 'redux/actions'
import { useRootStore } from 'stores/RootStore'
import { RootState, UserRole } from 'types'
import Styles from './WorkspaceDashboardSidebar.module.scss'
import { useLoggedIn } from 'hooks/useLoggedIn'
import { useSwipeable } from 'react-swipeable'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useGuestVisitingDoc } from 'hooks/useGuestVisitingDoc'
import { WorkspaceDashboardSidebarContent } from './WorkspaceDashboardSidebarContent'
import { SidebarResizeHandle } from './components/SidebarResizeHandle/SidebarResizeHandle'
import { generatePath, useHistory } from 'react-router-dom'
import { BLAZE_HOME_PATH } from 'paths'
import { NotificationsTrayContainer } from 'blaze-components/NotificationsTray/NotificationsTrayContainer'
import { Search } from 'blaze-ui/icons/20'
import { SearchModal } from 'screens/blaze/Search/SearchModal/SearchModal'
import { useModals } from 'almanac-ui/Modal'
import { LockButton } from './components/LockButton/LockButton'
import { Button } from 'blaze-components/WriterTopBar/Button'
import { NotificationsWriterTrigger } from 'blaze-components/NotificationsTray/NotificationsWriterTrigger'
import { CounterVariant } from 'blaze-ui/Counter/Counter'
import { CanView } from 'components/Can'
import { More } from './components/More/More'
import { PendingUpdatesContainer } from './components/PendingUpdatesContainer/PendingUpdatesContainer'
import { useCardDismissal } from 'hooks/queries/cardDismissals/useCardDismissal'
import { usePendingUpdates } from 'providers/UpdatesProvider/UpdatesProvider'
import CompanyLogo from 'components/CompanyLogo/CompanyLogo'
import { useIsAPFreemium } from 'hooks/useIsAPFreemium'
import { useBottomItemsCount } from 'hooks/useBottomItemsCount'
import { WorkspaceDropdown } from 'blaze-components/TopBar/WorkspaceDropdown/WorkspaceDropdown'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { PlanBadge } from './components/PlanBadge/PlanBadge'
import { ProfileDropdown } from 'blaze-components/TopBar/ProfileDropdown/ProfileDropdown'

export const WORKSPACE_DASHBOARD_SIDEBAR_ID = 'workspaceDashboardSidebar'

interface Props {
  floating?: boolean
  isLockable?: boolean
}

export const Sidebar = observer(({ floating = false, isLockable = true }: Props) => {
  const { isMobile, isMobileMd, isDesktop } = useResponsiveDesign()
  const sidebarRef = useRef<HTMLDivElement>(null)
  const history = useHistory()
  const { leftSidebarStore: sidebarStore } = useRootStore()
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const workspaces = useWorkspaces()
  const dispatch = useDispatch()
  const closeFloatingSidebar = useCallback(() => dispatch(setFloatingSidebar(false)), [dispatch])
  const isLoggedIn = useLoggedIn()
  const [isResponsiveLocked, setIsResponsiveLocked] = useState(false)
  const workspace = useCurrentWorkspace()
  const isAPFreemium = useIsAPFreemium()
  const guestVisitingDoc = useGuestVisitingDoc()
  const { openModal } = useModals()
  const hideSidebar = !workspace || guestVisitingDoc
  const pendingUpdates = usePendingUpdates()
  const bottomItemsCount = useBottomItemsCount()
  const { locked, floatingSidebar, focusMode } = useSelector(
    ({ floatingSidebar, focusMode, sidebar, sidebarWidth }: RootState) => ({
      floatingSidebar,
      focusMode,
      sidebarOpen: sidebar.locked || floatingSidebar,
      locked: sidebar.locked,
      sidebarWidth,
    }),
    isEqual,
  )
  const { data: referralCTA, isLoading: isLoadingReferralCTA } = useCardDismissal('left_rail_referral_cta')
  const isShowingReferralCTA = !isLoadingReferralCTA && !referralCTA?.is_dismissed && isMobile

  useEffect(() => {
    if (floating && isDesktop) {
      UserAppPreferencesService.unlockSidebar()
    }
    return () => {
      if (floating && isDesktop) {
        UserAppPreferencesService.lockSidebar()
      }
    }
  }, [floating]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isDesktop && locked) {
      if (!isLockable) return
      setIsResponsiveLocked(true)
      UserAppPreferencesService.unlockSidebar()
    } else if (isDesktop && isResponsiveLocked) {
      setIsResponsiveLocked(false)
      UserAppPreferencesService.lockSidebar()
    }
  }, [isDesktop]) // eslint-disable-line react-hooks/exhaustive-deps

  useLongHover(
    sidebarRef,
    () => {},
    (e: MouseEvent) => {
      // Hide sidebar only if mouse moved away from home button and not to the sidebar or the New popover.
      const relatedTarget = e.relatedTarget as HTMLElement
      const targetClasses = [...(relatedTarget?.classList || [])]
      const inPopover = targetClasses.some((c) => c.includes('ant-popover') || c.includes('NewButton'))
      if (!inPopover) closeFloatingSidebar()
    },
    200,
    200,
  )

  // Hook to close the floating sidebar if the user blurs the main window.
  // That way we keep the sidebar from being locked when the user leaves almanac
  // and having to manually unlock it when coming back.
  useEffect(() => {
    const onWindowBlur = () => closeFloatingSidebar()
    window.addEventListener('blur', onWindowBlur)
    return () => window.removeEventListener('blur', onWindowBlur)
  }, [closeFloatingSidebar])

  const handlers = useSwipeable({
    onSwipedLeft: (_eventData) => UserAppPreferencesService.unlockSidebar(),
    delta: 30,
  })

  const swipeableHandlers = useSwipeable({
    onSwipedRight: (_eventData) => UserAppPreferencesService.lockSidebar(),
    delta: 20,
    preventScrollOnSwipe: true,
  })

  const handleBackdropClick = () => {
    UserAppPreferencesService.unlockSidebar()
    closeFloatingSidebar()
  }

  const useResizableSidebar = locked && !isMobile
  const mobileWidth = isMobileMd ? '100vw' : 0
  let sidebarContainerWidthStyle: CSSProperties =
    useResizableSidebar && isDesktop && !floating ? { width: sidebarStore.sidebarWidth } : { width: mobileWidth }
  if (!isLockable && !isMobile) {
    if (!locked) UserAppPreferencesService.lockSidebar()
    sidebarContainerWidthStyle = { width: sidebarStore.sidebarWidth }
  }
  const sidebarWidthStyle: CSSProperties = useResizableSidebar ? { width: sidebarStore.sidebarWidth } : {}

  if (!workspace || hideSidebar) return null

  const companyLogo = () => {
    return <CompanyLogo red />
  }

  const footer = (
    <PendingUpdatesContainer
      noPendingUpdatesContent={
        <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
          <More />
        </CanView>
      }
    />
  )

  const isAPMobileWeb = workspace.is_virtual_marketer && isMobile

  return (
    <>
      {isMobileMd && <div className={Styles.swipeableHandle} {...swipeableHandlers}></div>}
      {isMobile && <div className={cx(Styles.backdrop, { [Styles.locked]: locked })} onClick={handleBackdropClick} />}
      <div
        data-sidebar-is-hovered={sidebarStore.isHovered}
        className={cx(Styles.sidebarContainer, {
          [Styles.isMobileMd]: isMobileMd,
          [Styles.locked]: locked,
          [Styles.floating]: floatingSidebar,
          [Styles.focusMode]: focusMode.showFocusMode,
          [Styles.resizable]: useResizableSidebar,
          [Styles.resizing]: sidebarStore.isResizing,
          [Styles.float]: floating,
        })}
        style={{
          ...sidebarContainerWidthStyle,
        }}
        {...handlers}
      >
        {!isMobile && isLockable && <div className={Styles.backdrop} onClick={handleBackdropClick} />}
        <div
          ref={sidebarRef}
          className={cx(Styles.sidebar, {
            [Styles.floating]: floatingSidebar,
            [Styles.locked]: locked,
            [Styles.focusMode]: focusMode.showFocusMode,
            [Styles.resizable]: useResizableSidebar,
            [Styles.resizing]: sidebarStore.isResizing,
          })}
          data-testid="workspace-dashboard-sidebar"
          id={WORKSPACE_DASHBOARD_SIDEBAR_ID}
          style={sidebarWidthStyle}
          onMouseEnter={() => sidebarStore.setIsHovered(true)}
        >
          {locked && !floating && <SidebarResizeHandle />}
          <div className={Styles.screen}>
            <div className={cx(Styles.header, { [Styles.apMobileWeb]: isAPMobileWeb })}>
              {isSystemRedesignEnabled ? (
                <WorkspaceDropdown selectedWorkspaceId={workspace.id} workspaces={workspaces} />
              ) : (
                <div
                  className={Styles.logoContainer}
                  onClick={() => history.push(generatePath(BLAZE_HOME_PATH, { workspaceId: workspace.id }))}
                >
                  {workspace.app_logo_url ? <img src={workspace.app_logo_url} alt={workspace.name} /> : companyLogo()}
                </div>
              )}
              {isAPMobileWeb ? (
                <>
                  <PlanBadge />
                  <div className={Styles.rectangle} />
                </>
              ) : null}
              {isMobile && (
                <div className={Styles.rightButtons}>
                  <Button icon={Search} ghost={isAPMobileWeb} onPress={() => openModal(SearchModal)} />
                  {isAPMobileWeb ? (
                    <ProfileDropdown selectedWorkspaceId={workspace.id} workspaces={workspaces} avatarSize={36} />
                  ) : null}
                  {workspace.is_virtual_marketer ? null : (
                    <NotificationsTrayContainer
                      trigger={<NotificationsWriterTrigger />}
                      counterVariant={CounterVariant.Dark}
                    />
                  )}
                </div>
              )}
              {!isMobile && isLockable && <LockButton />}
            </div>
            <div
              data-testid="sidebar-inner"
              className={cx(Styles.sidebarInner, {
                [Styles.showScrollbar]: sidebarStore.isHovering(),
              })}
            >
              <div
                className={cx(Styles.sidebarContent, {
                  [Styles.withReferralCta]: isShowingReferralCTA,
                  [Styles.withPendingUpdates]: pendingUpdates.length > 0,
                  [Styles.withFreemiumUsage]: isAPFreemium,
                  [Styles.apMobileWeb]: isAPMobileWeb,
                })}
                style={{ '--bottom-items-count': bottomItemsCount } as React.CSSProperties}
              >
                {isLoggedIn && <WorkspaceDashboardSidebarContent workspace={workspace} footer={footer} />}
              </div>

              {isLoggedIn && !isMobile && footer}
            </div>
          </div>
        </div>
      </div>
    </>
  )
})
